import { FilterIdsType } from '@app/components/common/FilterSelect/FilterSelect';
import { IInspectionsFilterOptions } from '@app/interfaces/inspection';
import { tableDataInit } from '@app/pages/FishingOperations/Inspections/constants';
import { formatFromFilter, formatToFilter } from '@app/utils/formatDate';
import { getUpdatedOptions } from '@app/utils/pageFilters';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';

export interface InspectionsFiltersState {
  filterOptions: IInspectionsFilterOptions;
  shouldTriggerFetch: boolean;
  onlyWithPenalties: boolean;
  onlyInvoiceNotPaid: boolean;
  selectedOrganizationalEntityIds: FilterIdsType;
  selectedWaterbodyIds: FilterIdsType;
  selectedInspectorIds: FilterIdsType;
  selectedLicenseIds: FilterIdsType;
  selectedFisherIds: FilterIdsType;
  fromFilterTemp: Dayjs | null | undefined;
  toFilterTemp: Dayjs | null | undefined;
  selectedOrganizationalEntityIdsTemp: FilterIdsType;
  selectedWaterbodyIdsTemp: FilterIdsType;
  selectedInspectorIdsTemp: FilterIdsType;
  selectedLicenseIdsTemp: FilterIdsType;
  selectedFisherIdsTemp: FilterIdsType;
}

const initialState: InspectionsFiltersState = {
  filterOptions: tableDataInit.filterOptions,
  shouldTriggerFetch: false,
  onlyWithPenalties: false,
  onlyInvoiceNotPaid: false,
  selectedOrganizationalEntityIds: [],
  selectedWaterbodyIds: [],
  selectedInspectorIds: [],
  selectedLicenseIds: [],
  selectedFisherIds: [],
  fromFilterTemp: undefined,
  toFilterTemp: undefined,
  selectedOrganizationalEntityIdsTemp: [],
  selectedWaterbodyIdsTemp: [],
  selectedInspectorIdsTemp: [],
  selectedLicenseIdsTemp: [],
  selectedFisherIdsTemp: [],
};

const inspectionsFiltersSlice = createSlice({
  name: 'inspectionsFilters',
  initialState,
  reducers: {
    setFilterOptions(state, action: PayloadAction<IInspectionsFilterOptions>) {
      state.filterOptions = action.payload;

      const availableOrganizationalEntityIds = getUpdatedOptions(
        state.selectedOrganizationalEntityIdsTemp,
        action.payload.organizationalEntities,
      );
      state.selectedOrganizationalEntityIdsTemp = availableOrganizationalEntityIds;
      state.selectedOrganizationalEntityIds = availableOrganizationalEntityIds;

      const availableWaterbodyIds = getUpdatedOptions(state.selectedWaterbodyIdsTemp, action.payload.waterbodies);
      state.selectedWaterbodyIdsTemp = availableWaterbodyIds;
      state.selectedWaterbodyIds = availableWaterbodyIds;

      const availableInspectorIds = getUpdatedOptions(state.selectedInspectorIdsTemp, action.payload.inspectors);
      state.selectedInspectorIdsTemp = availableInspectorIds;
      state.selectedInspectorIds = availableInspectorIds;

      const availableLicenseIds = getUpdatedOptions(state.selectedLicenseIdsTemp, action.payload.licenses);
      state.selectedLicenseIdsTemp = availableLicenseIds;
      state.selectedLicenseIds = availableLicenseIds;

      const availableFisherIds = getUpdatedOptions(state.selectedFisherIdsTemp, action.payload.fishers);
      state.selectedFisherIdsTemp = availableFisherIds;
      state.selectedFisherIds = availableFisherIds;
    },
    setShouldTriggerFetch(state, action: PayloadAction<boolean>) {
      state.shouldTriggerFetch = action.payload;
    },
    setOnlyWithPenalties(state, action: PayloadAction<boolean>) {
      state.onlyWithPenalties = action.payload;
      state.shouldTriggerFetch = true;
    },
    setOnlyInvoiceNotPaid(state, action: PayloadAction<boolean>) {
      state.onlyInvoiceNotPaid = action.payload;
      state.shouldTriggerFetch = true;
    },
    setSelectedOrganizationalEntityIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedOrganizationalEntityIds = action.payload;
    },
    setSelectedWaterbodyIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedWaterbodyIds = action.payload;
    },
    setSelectedInspectorIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedInspectorIds = action.payload;
    },
    setSelectedLicenseIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedLicenseIds = action.payload;
    },
    setSelectedFisherIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedFisherIds = action.payload;
    },
    setFromFilterTemp(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromFilterTemp = action.payload;
      state.shouldTriggerFetch = true;
    },
    setToFilterTemp(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toFilterTemp = action.payload;
      state.shouldTriggerFetch = true;
    },
    setSelectedOrganizationalEntityIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedOrganizationalEntityIdsTemp = action.payload;
    },
    setSelectedWaterbodyIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedWaterbodyIdsTemp = action.payload;
    },
    setSelectedInspectorIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedInspectorIdsTemp = action.payload;
    },
    setSelectedLicenseIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedLicenseIdsTemp = action.payload;
    },
    setSelectedFisherIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedFisherIdsTemp = action.payload;
    },
    resetQuickFilters(state) {
      state.onlyWithPenalties = initialState.onlyWithPenalties;
      state.onlyInvoiceNotPaid = initialState.onlyInvoiceNotPaid;
      state.shouldTriggerFetch = true;
    },
    resetFilters(state) {
      state.selectedOrganizationalEntityIds = initialState.selectedOrganizationalEntityIds;
      state.selectedWaterbodyIds = initialState.selectedWaterbodyIds;
      state.selectedInspectorIds = initialState.selectedInspectorIds;
      state.selectedLicenseIds = initialState.selectedLicenseIds;
      state.selectedFisherIds = initialState.selectedFisherIds;
      state.fromFilterTemp = initialState.fromFilterTemp;
      state.toFilterTemp = initialState.toFilterTemp;
      state.selectedOrganizationalEntityIdsTemp = initialState.selectedOrganizationalEntityIdsTemp;
      state.selectedWaterbodyIdsTemp = initialState.selectedWaterbodyIdsTemp;
      state.selectedInspectorIdsTemp = initialState.selectedInspectorIdsTemp;
      state.selectedLicenseIdsTemp = initialState.selectedLicenseIdsTemp;
      state.selectedFisherIdsTemp = initialState.selectedFisherIdsTemp;
      state.shouldTriggerFetch = true;
    },
  },
});

export const {
  setOnlyWithPenalties,
  setOnlyInvoiceNotPaid,
  setSelectedOrganizationalEntityIds,
  setSelectedWaterbodyIds,
  setSelectedInspectorIds,
  setSelectedLicenseIds,
  setSelectedFisherIds,
  setFromFilterTemp,
  setToFilterTemp,
  setSelectedOrganizationalEntityIdsTemp,
  setSelectedWaterbodyIdsTemp,
  setSelectedInspectorIdsTemp,
  setSelectedLicenseIdsTemp,
  setSelectedFisherIdsTemp,
} = inspectionsFiltersSlice.actions;

export const areQuickFiltersAppliedSelector = (state: { inspectionsFilters: InspectionsFiltersState }) => {
  const { onlyWithPenalties, onlyInvoiceNotPaid } = state.inspectionsFilters;

  return onlyWithPenalties || onlyInvoiceNotPaid;
};

export const areFiltersAppliedSelector = (state: { inspectionsFilters: InspectionsFiltersState }) => {
  const {
    selectedOrganizationalEntityIds,
    selectedWaterbodyIds,
    selectedInspectorIds,
    selectedLicenseIds,
    selectedFisherIds,
    fromFilterTemp,
    toFilterTemp,
  } = state.inspectionsFilters;

  return (
    !!selectedOrganizationalEntityIds.length ||
    !!selectedWaterbodyIds.length ||
    !!selectedInspectorIds.length ||
    !!selectedLicenseIds.length ||
    !!selectedFisherIds.length ||
    !!fromFilterTemp ||
    !!toFilterTemp
  );
};

export const activeFiltersCountSelector = (state: { inspectionsFilters: InspectionsFiltersState }) => {
  const {
    selectedOrganizationalEntityIds,
    selectedWaterbodyIds,
    selectedInspectorIds,
    selectedLicenseIds,
    selectedFisherIds,
    fromFilterTemp,
    toFilterTemp,
  } = state.inspectionsFilters;

  let count = 0;
  if (selectedOrganizationalEntityIds.length > 0) count++;
  if (selectedWaterbodyIds.length > 0) count++;
  if (selectedInspectorIds.length > 0) count++;
  if (selectedLicenseIds.length > 0) count++;
  if (selectedFisherIds.length > 0) count++;
  if (fromFilterTemp) count++;
  if (toFilterTemp) count++;

  return count;
};

export const fetchParamsSelector = (state: { inspectionsFilters: InspectionsFiltersState }) => {
  const {
    selectedOrganizationalEntityIds,
    selectedWaterbodyIds,
    selectedInspectorIds,
    selectedLicenseIds,
    selectedFisherIds,
    fromFilterTemp,
    toFilterTemp,
    onlyWithPenalties,
    onlyInvoiceNotPaid,
  } = state.inspectionsFilters;

  const fromFilterString = fromFilterTemp ? formatFromFilter(fromFilterTemp) : '';
  const toFilterString = toFilterTemp ? formatToFilter(toFilterTemp) : '';

  return {
    selectedOrganizationalEntityIds,
    selectedWaterbodyIds,
    selectedInspectorIds,
    selectedLicenseIds,
    selectedFisherIds,
    fromFilterString,
    toFilterString,
    onlyWithPenalties,
    onlyInvoiceNotPaid,
  };
};

export const inspectionsFiltersActions = inspectionsFiltersSlice.actions;

export const inspectionsFilters = inspectionsFiltersSlice.reducer;
